import React, { useCallback, useEffect, useState } from 'react';
import Universalpageheader from '../Headers/universalpageheader';
import { Button, Box, Typography, Paper, Divider, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayIcon from '@mui/icons-material/Replay';
import PaymentIcon from '@mui/icons-material/Payment';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Searchbar from '../Components/searchbar';
import LocationModal from '../Components/LocationModal';
import { imageurl, addKECode, isPhone, getManualDistance } from '../Utils/fn';
import { capitalizeFirst, currencyseperator } from '../Utils/functions';
import { setAlert } from '../../store/alert';
import fetch from '../Utils/fetch';

function Vieworderproduct() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const webSettings = useSelector((state) => state.webSettings.data);
    const subProducts = useSelector((state) => state.subProducts.data);
    const currentLocation = useSelector((state) => state.location.data);
    const { user } = useSelector((state) => state.user || {});

    const [showsearchbar, setshowsearchbar] = useState(false);
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [locationOpen, setLocationOpen] = useState(false);
    const [phone, setPhone] = useState(user?.mobileNumber ? user.mobileNumber.replace('+', '') : '254');
    const [paying, setPaying] = useState(false);

    // Geolocation state
    const [userCoords, setUserCoords] = useState(null);
    const [isDetectingLoc, setIsDetectingLoc] = useState(false);

    // Get order item from router state
    const [item, setItem] = useState(location.state?.item || location.state?.product);

    // Delivery fee settings
    const COST_PER_KM = parseFloat(webSettings?.COST_PER_KM || 0);
    const MINIMUM_DELIVERY_COST = parseFloat(webSettings?.MINIMUM_DELIVERY_COST || 0);
    const MAX_DELIVERY_RADIUS = parseFloat(webSettings?.MAX_DELIVERY_RADIUS || Infinity);

    // Detect user GPS
    const detectUserLocation = useCallback(() => {
        if (!navigator.geolocation) return;
        setIsDetectingLoc(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setUserCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
                setIsDetectingLoc(false);
            },
            (err) => {
                console.warn('Geolocation error:', err);
                setIsDetectingLoc(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        detectUserLocation();
    }, []);

    useEffect(() => {
        if (user?.mobileNumber) {
            setPhone(user.mobileNumber.replace('+', ''));
        }
    }, [user]);

    if (!item) {
        return (
            <div className="homebody">
                <Universalpageheader onshowsearch={() => setshowsearchbar(true)} pagename="Order Details" withbackicon={true} />
                <Box sx={{ p: 4, textAlign: 'center', mt: 5, mx: 2 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No order details found.
                    </Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                        Please navigate from the orders page to view item details.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/orders')}
                        startIcon={<ArrowBackIcon />}
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        Back to Orders
                    </Button>
                </Box>
            </div>
        );
    }

    const quantity = parseInt(item.quantity) || 1;
    const amountVal = parseFloat(item.amount_value) || 0;
    const unitPrice = amountVal / quantity;
    const orderSubtotal = parseFloat(item.order_amount) || amountVal;

    // Resolve the branch location object for this order
    const orderBranch = item.branch || item.location || currentLocation?.mainLocation;
    const selectedBranchObj = webSettings?.locations?.find(
        (loc) => loc.mainLocation === orderBranch
    ) || webSettings?.locations?.find(
        (loc) => loc.mainLocation === currentLocation?.mainLocation
    ) || webSettings?.locations?.[0];

    // Calculate radius and delivery fee
    let radius = 0;
    if (userCoords && selectedBranchObj?.latitude && selectedBranchObj?.longitude) {
        radius = parseFloat(
            getManualDistance(
                userCoords.lat,
                userCoords.lng,
                parseFloat(selectedBranchObj.latitude),
                parseFloat(selectedBranchObj.longitude)
            )
        );
    }
    const deliveryFee = radius > 0
        ? Math.max(radius * COST_PER_KM, MINIMUM_DELIVERY_COST)
        : MINIMUM_DELIVERY_COST;
    const grandTotal = orderSubtotal + deliveryFee;
    const isOutsideRadius = MAX_DELIVERY_RADIUS > 0 && radius > MAX_DELIVERY_RADIUS;

    const handleBuyAgain = () => {
        navigate('/product/' + item.item_url);
    };

    const handleSendPayment = async () => {
        if (isOutsideRadius) {
            dispatch(setAlert({ type: 'error', message: `You are outside the delivery zone (max ${MAX_DELIVERY_RADIUS} km).` }));
            return;
        }
        const phoneValidation = isPhone(phone);
        if (phoneValidation !== true) {
            dispatch(setAlert({ type: 'error', message: phoneValidation || 'Invalid phone number' }));
            return;
        }

        setPaying(true);
        const finalempesadata = {
            name: user?.fullName || 'Customer',
            order_no: item.order_number.toString(),
            order_type: (item.order_type || 'OFFLINE').toString(),
            amount: Math.ceil(grandTotal).toString(),
            phone_number: addKECode(phone).toString(),
            location: currentLocation?.mainLocation
        };

        try {
            const res = await fetch.post('pay-goods-online', finalempesadata);
            setPaying(false);
            setOpenPaymentModal(false);

            if (res && res.error === false) {
                dispatch(setAlert({ type: 'success', message: 'Payment request sent! Please check your phone.' }));
                
                // Wait 4 seconds then fetch updated orders
                setTimeout(() => {
                    dispatch(setAlert({ type: 'info', message: 'Checking payment status...' }));
                    fetch.get('getCustomerOrders').then(response => {
                        if (response && !response.error) {
                            const allItems = response.data.orders.flatMap(order =>
                                order.items.map(it => ({
                                    ...it,
                                    order_number: order.order_number,
                                    order_date: order.order_date,
                                    order_type: order.order_type,
                                    paid: order.payment_complete,
                                    order_reference: order.reference_number,
                                    order_amount: order.amount,
                                    branch: order.branch || order.location
                                }))
                            );
                            
                            const updatedItem = allItems.find(it => 
                                it.order_number === item.order_number && 
                                it.item_code === item.item_code
                            );
                            
                            if (updatedItem) {
                                setItem(updatedItem);
                                if (updatedItem.paid) {
                                    dispatch(setAlert({ type: 'success', message: 'Payment confirmed! Order status updated.' }));
                                } else {
                                    dispatch(setAlert({ type: 'warning', message: 'Payment check complete, but status is still pending.' }));
                                }
                            }
                        }
                    }).catch(error => {
                        console.error('Error fetching updated orders:', error);
                    });
                }, 4000);
            } else {
                console.log("wallah")
                dispatch(setAlert({ type: 'error', message: res.message || 'Payment request sent! Please check your phone.' }));
            }
        } catch (err) {
            setPaying(false);
            dispatch(setAlert({ type: 'error', message: typeof err === 'object' ? err.message : err }));
        }
    };

    return (
        <div className="homebody">
            {showsearchbar && <Searchbar dismissmodals={() => setshowsearchbar(false)} products={subProducts?.flatMap((obj) => obj.items)} />}
            <Universalpageheader onshowsearch={() => setshowsearchbar(true)} pagename="Order Item" withbackicon={true} />
            <LocationModal open={locationOpen} onClose={() => setLocationOpen(false)} />

            <Box sx={{ p: 2, pb: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* Current Delivery Location */}
                <Paper
                    elevation={0}
                    onClick={() => setLocationOpen(true)}
                    sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'primary.light',
                        bgcolor: '#f0f7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#e3f0ff' },
                        transition: 'background 0.2s'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <PlaceOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, display: 'block' }}>
                                Delivery from
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.4 }}>
                                {currentLocation?.locationName || selectedBranchObj?.locationName || 'Select branch'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                        <EditLocationAltIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Change</Typography>
                    </Box>
                </Paper>

                {/* Product Main Card */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 2,
                        background: 'linear-gradient(to bottom, #ffffff, #fdfdfd)'
                    }}
                >
                    <Box
                        sx={{
                            width: 140,
                            height: 140,
                            borderRadius: 3,
                            overflow: 'hidden',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'grey.100'
                        }}
                    >
                        <img
                            src={imageurl(item.src?.[0])}
                            alt={item.item_name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/150'; }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, px: 1, lineHeight: 1.3 }}>
                            {capitalizeFirst(item.item_name)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {item.group_name}
                        </Typography>
                    </Box>
                </Paper>

                {/* Pending Payment Action Banner */}
                {!item.paid && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 4,
                            border: '1px solid',
                            borderColor: isOutsideRadius ? 'error.main' : 'warning.main',
                            bgcolor: isOutsideRadius ? '#fff3f3' : 'warning.main' + '15',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PaymentIcon sx={{ color: isOutsideRadius ? 'error.main' : 'warning.main' }} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isOutsideRadius ? 'error.dark' : 'warning.dark' }}>
                                    {isOutsideRadius ? 'Outside Delivery Zone' : 'Payment Pending'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {isOutsideRadius
                                        ? `You are ${radius.toFixed(1)} km away, exceeding the ${MAX_DELIVERY_RADIUS} km delivery limit.`
                                        : 'This order has not been paid yet. Please complete payment using M-Pesa.'
                                    }
                                </Typography>
                            </Box>
                        </Box>
                        {!isOutsideRadius && (
                            <Button
                                variant="contained"
                                color="warning"
                                fullWidth
                                startIcon={<PaymentIcon />}
                                onClick={() => setOpenPaymentModal(true)}
                                sx={{
                                    borderRadius: 2.5,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    color: 'white',
                                    bgcolor: 'warning.main',
                                    '&:hover': {
                                        bgcolor: 'warning.dark',
                                    }
                                }}
                            >
                                Pay via M-Pesa Now ({webSettings.CURRENCY} {currencyseperator(Math.ceil(grandTotal))})
                            </Button>
                        )}
                    </Paper>
                )}

                {/* Order Information */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5,
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShoppingBagIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                        ORDER INFORMATION
                    </Typography>

                    <Stack spacing={2.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Order Number</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, bgcolor: 'action.selected', px: 1.5, py: 0.5, borderRadius: 1.5 }}>
                                #{item.order_number}
                            </Typography>
                        </Box>

                        <Divider />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                            <Chip
                                icon={item.paid ? <CheckCircleIcon sx={{ fontSize: '1rem !important' }} /> : <PendingActionsIcon sx={{ fontSize: '1rem !important' }} />}
                                label={item.paid ? "Paid" : "Pending"}
                                color={item.paid ? "success" : "warning"}
                                size="small"
                                sx={{ fontWeight: 600, borderRadius: 1.5 }}
                            />
                        </Box>

                        <Divider />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Order Date</Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <CalendarTodayIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {new Date(item.order_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                </Typography>
                            </Stack>
                        </Box>

                        <Divider />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="body2" color="text.secondary">Reference Number</Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <ReceiptLongIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all', textAlign: 'right', maxWidth: 180 }}>
                                    {item.reference_number || item.order_reference || 'N/A'}
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>
                </Paper>

                {/* Price Details */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5,
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.secondary' }}>
                        PRICE DETAILS
                    </Typography>

                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Unit Price</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {webSettings.CURRENCY} {currencyseperator(unitPrice.toFixed(2))}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Quantity</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {quantity} {item.suom || 'Unit(s)'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {webSettings.CURRENCY} {currencyseperator(orderSubtotal.toFixed(2))}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Delivery Fee
                                {radius > 0 && (
                                    <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }}>
                                        ({radius.toFixed(1)} km)
                                    </Typography>
                                )}
                                {isDetectingLoc && (
                                    <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }}>
                                        (detecting...)
                                    </Typography>
                                )}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {isDetectingLoc
                                    ? '...'
                                    : radius > 0
                                        ? `${webSettings.CURRENCY} ${currencyseperator(deliveryFee.toFixed(2))}`
                                        : '--'
                                }
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total Amount</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 800 }}>
                                {webSettings.CURRENCY} {currencyseperator((radius > 0 ? grandTotal : orderSubtotal).toFixed(2))}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        startIcon={<ReplayIcon />}
                        onClick={handleBuyAgain}
                        sx={{
                            borderRadius: 3,
                            py: 1.5,
                            fontWeight: 700,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }}
                    >
                        Buy Again
                    </Button>

                    <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        onClick={() => navigate('/orders')}
                        sx={{
                            borderRadius: 3,
                            py: 1.5,
                            fontWeight: 600,
                            borderColor: 'divider',
                            color: 'text.primary',
                            '&:hover': {
                                borderColor: 'text.primary',
                                bgcolor: 'action.hover'
                            }
                        }}
                    >
                        Back to Orders
                    </Button>
                </Box>

            </Box>

            {/* M-Pesa Payment Dialog */}
            <Dialog
                open={openPaymentModal}
                onClose={() => !paying && setOpenPaymentModal(false)}
                PaperProps={{
                    sx: { borderRadius: 4, p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    M-Pesa Payment
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 1 }}>
                        Please enter your Safaricom phone number to receive the M-Pesa STK Push.
                    </DialogContentText>
                    {/* Payment breakdown */}
                    <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5, mb: 2, fontSize: 13 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Order Subtotal</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>{webSettings.CURRENCY} {currencyseperator(orderSubtotal.toFixed(2))}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Delivery Fee {radius > 0 ? `(${radius.toFixed(1)} km)` : ''}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {radius > 0 ? `${webSettings.CURRENCY} ${currencyseperator(deliveryFee.toFixed(2))}` : '--'}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 0.75 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>Total to Pay</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                {webSettings.CURRENCY} {currencyseperator(Math.ceil(grandTotal))}
                            </Typography>
                        </Box>
                    </Box>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="phone"
                        label="M-Pesa Phone Number"
                        type="tel"
                        fullWidth
                        variant="outlined"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={paying}
                        placeholder="07XXXXXXXX"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setOpenPaymentModal(false)}
                        disabled={paying}
                        sx={{ fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSendPayment}
                        variant="contained"
                        disabled={paying || isOutsideRadius}
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                    >
                        {paying ? <CircularProgress size={24} color="inherit" /> : 'Send STK Push'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Vieworderproduct;
